/**
 * Top-level table for the "Properties" view mode. Renders the property list (the same data
 * shown by the card view) as a flat table with Address, Type, and Units columns plus a
 * per-row overflow menu. Rows navigate to the property when clicked.
 */
import { useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Paragraph, PropertyCard, Table, VisuallyHidden } from '@zillow/constellation-10';

import { useTableObserver } from '../../../hooks/queries/properties/useTableObserver';
import { useScreenSize } from '../../../hooks/useScreenSize';
import {
  getAddressLines,
  getPhotoThumbUrl,
  getPropertyTypeLabel,
  getUnitSummary,
} from '../../../utils/flexiblePropertyCardUtils';

import { getDashboardOverviewRoute, getListingRoute } from '../../../constants/urls';

import ArchivePropertyModal from '../../../components/modals/ArchivePropertyModal';
import { FixedScrollbar } from '../../../components/properties/FixedScrollbar';
import { TruncatedTextWithTooltip } from '../../../components/TruncatedTextWithTooltip';

import {
  hiddenMobileCellStyles,
  lastRowStyles,
  overflowMenuCellStyle,
  propertyTableCellContentWidths,
  propertyThumbnailStyles,
  truncatedTextStyles,
  unitCellContentStyles,
  unitInfoStyles,
} from '../styles';

import { FlexiblePropertyCardOverflowMenu } from './FlexiblePropertyCardOverflowMenu';
import SkeletonPropertyRows from './SkeletonPropertyRows';

import type { AccountViewListingGroupDto, RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';

// Secondary columns are hidden on small screens, keeping only Address and the always-visible
// overflow menu (which has no visible header label).
const PROPERTY_TABLE_COLUMNS: Array<{
  label: string;
  hideOnMobile?: boolean;
  hideLabel?: boolean;
  width?: (typeof propertyTableCellContentWidths)[keyof typeof propertyTableCellContentWidths];
}> = [
  { label: 'Address', width: propertyTableCellContentWidths.ADDRESS },
  { label: 'Type', hideOnMobile: true, width: propertyTableCellContentWidths.TYPE },
  { label: 'Units', hideOnMobile: true, width: propertyTableCellContentWidths.UNITS },
  { label: 'Overflow menu', hideLabel: true, width: propertyTableCellContentWidths.OVERFLOW },
];

type ArchiveTarget = {
  listingAlias: string;
  address: RentalManagerAddressDto;
};

type Props = {
  listingGroups: Array<AccountViewListingGroupDto>;
  isLoading?: boolean;
};

const PropertiesTable = ({ listingGroups, isLoading = false }: Props) => {
  const router = useRouter();
  const { isScreenMobile } = useScreenSize();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { showFixedScrollbar, isTableBottomVisible } = useTableObserver({ scrollContainerRef, isScreenMobile });

  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);

  const showSkeletons = isLoading && listingGroups.length === 0;

  const handleRowClick = (listing: AccountViewListingGroupDto) => {
    if (!listing.listingAlias) {
      return;
    }

    const route = listing.isMultifamily
      ? getListingRoute({ listingAlias: listing.listingAlias })
      : getDashboardOverviewRoute({ listingAlias: listing.listingAlias });

    router.push(route);
  };

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>, listing: AccountViewListingGroupDto) => {
    // Only navigate when the row itself is focused, not a nested control like the overflow menu.
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleRowClick(listing);
    }
  };

  return (
    <Box css={{ overflow: { base: 'auto', lg: 'hidden' } }}>
      <Box ref={scrollContainerRef} css={{ overflowX: { base: 'hidden', lg: 'auto' }, maxWidth: '100%' }}>
        <Table.Root
          data-testid="PropertiesTable"
          aria-label="Properties"
          appearance="horizontal"
          css={{ width: '100%', tableLayout: 'fixed' }}
        >
          {/* header */}
          <Table.Header>
            <Table.Row>
              {PROPERTY_TABLE_COLUMNS.map((column) => (
                <Table.HeaderCell
                  key={column.label}
                  css={{
                    ...(column.hideOnMobile ? hiddenMobileCellStyles : {}),
                    ...(column.width ?? {}),
                  }}
                >
                  {column.hideLabel ? (
                    <VisuallyHidden>{column.label}</VisuallyHidden>
                  ) : (
                    <Paragraph textStyle="body-sm-bold">{column.label}</Paragraph>
                  )}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {/* Skeletons */}
            {showSkeletons && <SkeletonPropertyRows />}

            {/* Listings */}
            {!showSkeletons &&
              listingGroups.map((listing, rowIndex) => {
                const isLastRow = rowIndex === listingGroups.length - 1;
                const { streetLine, cityStateZip } = getAddressLines(listing);

                return (
                  <Table.Row
                    key={listing.groupId}
                    tabIndex={0}
                    onClick={() => handleRowClick(listing)}
                    onKeyDown={(event) => handleRowKeyDown(event, listing)}
                    css={{ cursor: 'pointer', ...(isLastRow ? lastRowStyles : {}) }}
                  >
                    {/* address */}
                    <Table.Cell>
                      <Box css={unitCellContentStyles}>
                        <Box css={propertyThumbnailStyles}>
                          <PropertyCard.Photo
                            src={getPhotoThumbUrl(listing.photoKey)}
                            alt={streetLine || 'Property'}
                            fallbackImg={<PropertyCard.PhotoFallback />}
                            css={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </Box>
                        <Box css={unitInfoStyles}>
                          <TruncatedTextWithTooltip
                            text={streetLine || '—'}
                            textStyle="body-sm-bold"
                            css={truncatedTextStyles}
                          />
                          <TruncatedTextWithTooltip
                            text={cityStateZip}
                            textStyle="body-xs"
                            color="text.subtle"
                            css={truncatedTextStyles}
                          />
                        </Box>
                      </Box>
                    </Table.Cell>

                    {/* type */}
                    <Table.Cell css={hiddenMobileCellStyles}>
                      <Paragraph textStyle="body-sm">{getPropertyTypeLabel(listing) ?? '—'}</Paragraph>
                    </Table.Cell>

                    {/* units */}
                    <Table.Cell css={hiddenMobileCellStyles}>
                      <Paragraph textStyle="body-sm">{getUnitSummary(listing)}</Paragraph>
                    </Table.Cell>

                    {/* overflow menu */}
                    <Table.Cell css={overflowMenuCellStyle}>
                      <FlexiblePropertyCardOverflowMenu
                        triggerLabel={streetLine ? `Options for ${streetLine}` : 'Property options'}
                        items={[
                          // TODO: wire up the edit property action.
                          { label: 'Edit property' },
                          ...(listing.listingAlias
                            ? [
                                {
                                  label: 'Archive property',
                                  onSelect: () =>
                                    setArchiveTarget({
                                      listingAlias: listing.listingAlias,
                                      address: listing.address,
                                    }),
                                },
                              ]
                            : []),
                        ]}
                      />
                    </Table.Cell>
                  </Table.Row>
                );
              })}
          </Table.Body>
        </Table.Root>
      </Box>

      <FixedScrollbar
        scrollContainerRef={scrollContainerRef}
        isVisible={showFixedScrollbar}
        isTableBottomVisible={isTableBottomVisible}
      />

      {archiveTarget && (
        <ArchivePropertyModal
          onClose={() => setArchiveTarget(null)}
          isArchived={false}
          listingAlias={archiveTarget.listingAlias}
          address={archiveTarget.address}
        />
      )}
    </Box>
  );
};

export default PropertiesTable;
